/**
 * One goal row: ⠿ grip (rank by drag), inline title / weekly budget / next
 * step, the audience control (Me | AI team | Both, optionally narrowed to one
 * employee), this week's plan vs. actual, and the status actions. Achieved
 * and Retire are two-step — both take the goal's future blocks with them.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clsx } from "clsx";

import type { FocusAudience, FocusGoal, FocusGoalStatus, GoalPatchBody, SeatRef } from "../../api/focus";
import { AUDIENCES, STATUS_TONE } from "../../lib/focus";
import { formatMinutes, parseDuration } from "../../lib/focusTime";
import { EditableField } from "../EditableField";
import { Pill } from "../Pill";

const CONFIRM_MS = 4_000;

export function SortableGoalRow(props: GoalRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.goal.id,
  });
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li ref={setNodeRef} style={style}>
      <GoalRow
        {...props}
        grip={
          <button
            type="button"
            aria-label="Drag to reorder"
            className="goal-grip"
            {...attributes}
            {...listeners}
          >
            ⠿
          </button>
        }
      />
    </li>
  );
}

export interface GoalRowProps {
  goal: FocusGoal;
  seats: SeatRef[];
  busy: boolean;
  /** Resolves on success; rejects with a readable Error (EditableField shows it). */
  onPatch: (goal: FocusGoal, patch: GoalPatchBody) => Promise<unknown>;
}

export function GoalRow({ goal, seats, busy, onPatch, grip }: GoalRowProps & { grip?: ReactNode }) {
  const archived = goal.status === "achieved" || goal.status === "retired";
  const week = goal.week;
  const notPlannableWhy =
    goal.plannable || archived
      ? null
      : goal.audience === "ai"
        ? "AI team only – you plan no time for it"
        : goal.status === "paused"
          ? "Paused – resume to plan time"
          : "Not plannable";

  return (
    <div className={clsx("goal-row", archived && "is-archived", goal.status === "paused" && "is-paused")}>
      <div className="goal-row-grip">{grip}</div>
      <div className="goal-row-rank">{archived ? "–" : (goal.rank ?? "–")}</div>
      <div className="goal-row-main">
        <div className="goal-row-title">
          <EditableField
            label="Title"
            value={goal.title}
            disabled={busy}
            onSave={(title) => {
              if (!title) return Promise.reject(new Error("A goal needs a title"));
              return onPatch(goal, { title });
            }}
          />
        </div>
        <div className="goal-row-next">
          <span className="goal-row-key">next</span>
          <div className="min-w-0 flex-1">
            <EditableField
              label="Next step"
              value={goal.next_step}
              placeholder="The next concrete step"
              disabled={busy}
              renderValue={(v) => v || <span className="text-ink-low">add a next step</span>}
              onSave={(next) => onPatch(goal, { next_step: next || null })}
            />
          </div>
        </div>
        <div className="goal-row-badges">
          {goal.status !== "active" ? <Pill value={goal.status} tone={STATUS_TONE[goal.status]} /> : null}
          {notPlannableWhy ? (
            <span title={notPlannableWhy}>
              <Pill value="not plannable" tone="muted" />
            </span>
          ) : null}
          {goal.has_newer_draft ? (
            <span title="An employee proposed a newer version – review it on the Team page">
              <Pill value="draft pending" tone="signal" />
            </span>
          ) : null}
        </div>
      </div>
      <div className="goal-row-audience">
        <AudienceControl goal={goal} seats={seats} disabled={busy || archived} onPatch={onPatch} />
      </div>
      <div className="goal-row-budget">
        <span className="goal-row-key">week</span>
        <EditableField
          label="Weekly budget"
          value={goal.weekly_minutes != null ? formatMinutes(goal.weekly_minutes) : ""}
          placeholder="e.g. 4h, 1:30, 90"
          disabled={busy || archived}
          renderValue={(v) => v || <span className="text-ink-low">no budget</span>}
          onSave={(raw) => {
            if (!raw) return onPatch(goal, { weekly_minutes: null });
            const minutes = parseDuration(raw);
            if (minutes === null || minutes > 10080) {
              return Promise.reject(new Error("Use 90, 1:30 or 1.5h"));
            }
            return onPatch(goal, { weekly_minutes: minutes });
          }}
        />
        {week && !archived ? (
          <div className="goal-row-stats">
            {formatMinutes(week.planned_minutes)} planned · {formatMinutes(week.actual_minutes)} done
          </div>
        ) : null}
      </div>
      <div className="goal-row-actions">
        <StatusActions goal={goal} busy={busy} onPatch={onPatch} />
      </div>
    </div>
  );
}

function AudienceControl({
  goal,
  seats,
  disabled,
  onPatch,
}: {
  goal: FocusGoal;
  seats: SeatRef[];
  disabled: boolean;
  onPatch: GoalRowProps["onPatch"];
}) {
  const assigned = goal.assigned_agent?.id ?? "";
  const assignedMissing = assigned && !seats.some((s) => s.id === assigned);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="segmented segmented--compact" role="radiogroup" aria-label="Audience">
        {AUDIENCES.map((a) => (
          <button
            key={a.value}
            type="button"
            role="radio"
            aria-checked={goal.audience === a.value}
            title={a.value === "ai" ? `${a.hint} – its future blocks are removed` : a.hint}
            disabled={disabled}
            className={clsx("segmented-item", goal.audience === a.value && "is-active")}
            onClick={() => {
              if (goal.audience === a.value) return;
              const patch: GoalPatchBody = { audience: a.value as FocusAudience };
              if (a.value === "me") patch.assigned_agent_id = null;
              void onPatch(goal, patch).catch(() => undefined);
            }}
          >
            {a.label}
          </button>
        ))}
      </div>
      {goal.audience !== "me" ? (
        <select
          className="input input--compact"
          aria-label="Which employee"
          value={assigned}
          disabled={disabled}
          onChange={(e) =>
            void onPatch(goal, { assigned_agent_id: e.target.value || null }).catch(() => undefined)
          }
        >
          <option value="">whole AI team</option>
          {assignedMissing && goal.assigned_agent ? (
            <option value={assigned}>{goal.assigned_agent.name ?? goal.assigned_agent.slug}</option>
          ) : null}
          {seats.map((s) => (
            <option key={s.id} value={s.id} disabled={s.disabled && s.id !== assigned}>
              {s.name ?? s.slug}
              {s.disabled ? " (suspended)" : ""}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

function StatusActions({
  goal,
  busy,
  onPatch,
}: {
  goal: FocusGoal;
  busy: boolean;
  onPatch: GoalRowProps["onPatch"];
}) {
  const [confirming, setConfirming] = useState<FocusGoalStatus | null>(null);
  useEffect(() => {
    if (!confirming) return;
    const t = window.setTimeout(() => setConfirming(null), CONFIRM_MS);
    return () => window.clearTimeout(t);
  }, [confirming]);

  const go = (status: FocusGoalStatus) => {
    setConfirming(null);
    void onPatch(goal, { status }).catch(() => undefined);
  };

  if (goal.status === "achieved" || goal.status === "retired") {
    return (
      <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => go("active")}>
        Reopen
      </button>
    );
  }
  if (confirming) {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="btn btn-sm btn-main"
          disabled={busy}
          title="Its future blocks leave your calendar"
          onClick={() => go(confirming)}
        >
          {confirming === "achieved" ? "Mark achieved" : "Retire"} – drop future blocks
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirming(null)}>
          Cancel
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 flex-wrap justify-end">
      {goal.status === "paused" ? (
        <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => go("active")}>
          Resume
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          disabled={busy}
          title="Planned blocks stay; the goal can't take new ones"
          onClick={() => go("paused")}
        >
          Pause
        </button>
      )}
      <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setConfirming("achieved")}>
        Achieved
      </button>
      <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setConfirming("retired")}>
        Retire
      </button>
    </div>
  );
}
